import { FormRendererAPIType } from 'components/common/form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { formAPI } from 'api/form';
import React, { useEffect } from 'react';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { SiblingTabType } from 'app/extreport/viewer/SiblingTabsMenu';
import { DashboardElementType } from 'tools/types';
import ExternalReportFiltersForm from './form/ExternalReportFiltersForm';
import InternalReportFiltersForm from './form/InternalReportFiltersForm';
import { IFiltersFormProps } from './form';
import { useInternalReportFiltersInitialData } from './form/useInternalReportFiltersInitialData';
import { ComponentSettings } from '@report-content';

export default function FiltersFormWrapper({
    elementId,
    segmentValueId,
    elementType,
    externalId,
    listId,
    setForm,
    itemId,
    hasFilters,
    multipleRefsLabel,
    aiInsightsSelectionError,
}: {
    elementId: number;
    segmentValueId: number;
    elementType: DashboardElementType;
    externalId: number;
    listId: number;
    setForm: (form: FormRendererAPIType) => void;
    itemId: number;
    hasFilters: boolean;
    multipleRefsLabel?: string;
    aiInsightsSelectionError?: boolean;
}) {
    const queryClient = useQueryClient();

    let burstQueryKey = ['burstFilter', elementId, segmentValueId, externalId];
    const { data } = useQuery<any | void, Error>({
        queryKey: burstQueryKey,
        queryFn: () =>
            formAPI
                .load(
                    `/data/notification/distribution-list-item/${elementId}/${segmentValueId}/${externalId}/${listId}/form`,
                )
                .then((response: any) => response?.data?.data),
    });

    let tabsDataQueryKey = ['burstFilterTabs', elementId, segmentValueId, externalId, itemId];
    const { data: tabsData } = useQuery<Array<SiblingTabType> | void, Error>({
        queryKey: tabsDataQueryKey,
        queryFn: () =>
            elementType == 'external report'
                ? formAPI
                      .load(`/data/notification/distribution-list-item/${listId}/${itemId}`)
                      .then((response: any) => response?.data?.data ?? [])
                : [],
    });

    useEffect(() => {
        return () => {
            queryClient.removeQueries({ queryKey: burstQueryKey });
            queryClient.removeQueries({ queryKey: tabsDataQueryKey });
        };
    }, []);

    const { initialData, contentSettings, isLoading } = useInternalReportFiltersInitialData(
        elementId,
        segmentValueId,
        elementType,
        data?.segmentData,
        data?.filtersData as ComponentSettings,
    );

    if (!data || !tabsData) {
        return <LoadingPlaceholder />;
    }

    const props: IFiltersFormProps = {
        tabsData: tabsData,
        setForm: setForm,
        segmentData: data.segmentData,
        elementId: elementId,
        segmentValueId: segmentValueId,
        externalId: externalId,
        defaultData: data.defaults,
        formValues: data.values,
        multipleRefsLabel: multipleRefsLabel,
        hasFilters: hasFilters,
        aiInsights: data?.aiInsights ?? [],
        aiInsightsSelectionError: aiInsightsSelectionError,
    };

    if (elementType == 'internal report') {
        return !initialData || isLoading ? (
            <LoadingPlaceholder />
        ) : (
            <InternalReportFiltersForm
                {...props}
                manageFiltersSettings={data.filtersData}
                manageFiltersInitialData={initialData}
                contentSettings={contentSettings}
            />
        );
    } else {
        return <ExternalReportFiltersForm {...props} viewerFilerData={data.filtersData.data} />;
    }
}
